/*
* <license header>
*/

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { style } from '@react-spectrum/s2/style' with { type: 'macro' }
import {
  ActionButton,
  Avatar,
  Badge,
  MenuTrigger,
  Menu,
  MenuItem,
  MenuSection,
  Header,
  Heading,
  Text,
  Tooltip,
  TooltipTrigger
} from '@react-spectrum/s2'
import User from "@react-spectrum/s2/icons/User"
import InfoCircle from "@react-spectrum/s2/icons/InfoCircle"
import Leave from "@react-spectrum/s2/icons/Leave"
import UserGroup from "@react-spectrum/s2/icons/UserGroup"
import UserSettings from "@react-spectrum/s2/icons/UserSettings"
import UserLock from "@react-spectrum/s2/icons/UserLock"
import Checkmark from "@react-spectrum/s2/icons/Checkmark"
import { IMS } from '../../types'
import { useAuth } from '../../contexts/AuthContext'
import { useProfileAvatar } from '../../hooks/useProfileAvatar'
import { useHasAnyPermission } from '../../hooks/useHasPermission'
import { useGroup } from '../../contexts/GroupContext'

interface UserPanelProps {
  ims: IMS
  compact?: boolean  // For top nav layout
}

export const UserPanel: React.FC<UserPanelProps> = ({ ims, compact = false }) => {
  const navigate = useNavigate()
  const { signOut, authMode } = useAuth()
  const { avatarUrl } = useProfileAvatar(ims)

  // RBAC group switching
  const { groups, activeGroup, setActiveGroup } = useGroup()

  // RBAC admin checks
  const canManageAccess = useHasAnyPermission([['scope', 'read'], ['group', 'read']])
  const canManageRoles = useHasAnyPermission([['role', 'read']])
  const showAdminSection = canManageAccess || canManageRoles

  // Extract user info from IMS
  const userName = ims.profile?.name || 'Guest User'
  const userEmail = ims.profile?.email || ''

  // Get initials for avatar
  const getInitials = (name: string): string => {
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const handleMenuAction = (key: React.Key) => {
    if (key === 'profile') {
      navigate('/profile')
    } else if (key === 'signout') {
      signOut()
    } else if (key === 'access') {
      navigate('/access')
    } else if (key === 'roles') {
      navigate('/roles')
    } else if (typeof key === 'string' && key.startsWith('group_')) {
      setActiveGroup(key.replace('group_', ''))
    }
  }

  // If no IMS profile, show minimal panel
  const panelShell = (children: React.ReactNode) => (
    <div
      className={compact ? 'user-panel-compact' : ''}
      style={{
        background: compact ? 'transparent' : 'var(--spectrum-global-color-gray-100)',
        padding: compact ? 8 : 16,
        borderRadius: 8,
        marginBottom: compact ? 0 : 24,
      }}
    >
      {children}
    </div>
  )

  if (!ims.profile) {
    return panelShell(
      <div className={style({ display: 'flex', alignItems: 'center', gap: 12 })}>
        <div
          className="user-avatar"
          style={{
            background: 'var(--spectrum-global-color-blue-600)',
            width: 32,
            height: 32,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Text UNSAFE_className="user-initials-compact">GU</Text>
        </div>
        <Text UNSAFE_className="user-name">Guest User</Text>
      </div>
    )
  }

  let compactGroupIndicator: React.ReactNode = null
  if (compact && activeGroup) {
    const badge = (
      <div className={style({ flexShrink: 0 })}>
        <Badge
          data-testid="active-group-badge"
          variant="informative"
          fillStyle="subtle"
          size="S"
          aria-label={`Active group: ${activeGroup.name}`}
          UNSAFE_style={{ whiteSpace: 'nowrap' }}
        >
          <UserGroup />
          <Text UNSAFE_style={{ whiteSpace: 'nowrap' }}>{activeGroup.name}</Text>
        </Badge>
      </div>
    )
    compactGroupIndicator = activeGroup.scopeName ? (
      <TooltipTrigger delay={0}>
        {badge}
        <Tooltip>
          <Text>{activeGroup.scopeName}</Text>
        </Tooltip>
      </TooltipTrigger>
    ) : (
      badge
    )
  }

  return panelShell(
    <>
      <div
        className={
          compact
            ? style({ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 })
            : style({ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 })
        }
      >
        {compactGroupIndicator}
        <MenuTrigger>
          <ActionButton
            data-testid="user-panel"
            isQuiet
            UNSAFE_className={compact ? 'user-panel-button-compact' : 'user-panel-button'}
          >
            <div className={style({ display: 'flex', alignItems: 'center', gap: 12, width: '[100%]' })}>
              {/* Avatar: image when available, else initials */}
              {avatarUrl ? (
                <Avatar
                  size={compact ? 32 : 40}
                  src={avatarUrl}
                  alt={userName}
                />
              ) : (
                <div
                  className="user-avatar"
                  style={{
                    background: 'var(--spectrum-global-color-blue-600)',
                    width: compact ? 32 : 40,
                    height: compact ? 32 : 40,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text UNSAFE_className={compact ? 'user-initials-compact' : 'user-initials'}>
                    {getInitials(userName)}
                  </Text>
                </div>
              )}

              {/* User info - only show name in compact mode */}
              <div className={`${style({ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 })} user-info-container`}>
                <Text UNSAFE_className="user-name">
                  {userName}
                </Text>
                {!compact && userEmail && (
                  <Text UNSAFE_className="user-email">
                    {userEmail}
                  </Text>
                )}
              </div>
            </div>
          </ActionButton>

          <Menu onAction={handleMenuAction} UNSAFE_style={{ minWidth: '240px' }}>
            <MenuSection>
              <MenuItem id="profile" textValue="View Profile">
                <User />
                <Text slot="label">View Profile</Text>
              </MenuItem>
            </MenuSection>
            {groups.length > 0 ? (
              <MenuSection>
                <Header>
                  <Heading>Group</Heading>
                </Header>
                {groups.map(group => (
                  <MenuItem
                    key={`group_${group.groupId}`}
                    id={`group_${group.groupId}`}
                    textValue={group.name}
                  >
                    {activeGroup?.groupId === group.groupId ? <Checkmark /> : <UserGroup />}
                    <Text slot="label">{group.name}</Text>
                    {group.scopeName && <Text slot="description">{group.scopeName}</Text>}
                  </MenuItem>
                ))}
              </MenuSection>
            ) : null}
            {showAdminSection ? (
              <MenuSection>
                <Header>
                  <Heading>Administration</Heading>
                </Header>
                {canManageAccess ? (
                  <MenuItem id="access" textValue="Access Management">
                    <UserSettings />
                    <Text slot="label">Access Management</Text>
                  </MenuItem>
                ) : null}
                {canManageRoles ? (
                  <MenuItem id="roles" textValue="Roles">
                    <UserLock />
                    <Text slot="label">Roles</Text>
                  </MenuItem>
                ) : null}
              </MenuSection>
            ) : null}
            {authMode === 'standalone' ? (
              <MenuSection>
                <MenuItem id="signout" textValue="Sign Out">
                  <Leave />
                  <Text slot="label">Sign Out</Text>
                </MenuItem>
              </MenuSection>
            ) : (
              <MenuSection aria-label="shell-mode">
                <MenuItem id="shell-info" textValue="Managed by Experience Cloud">
                  <InfoCircle />
                  <Text slot="label">Sign out via Experience Cloud</Text>
                </MenuItem>
              </MenuSection>
            )}
          </Menu>
        </MenuTrigger>
      </div>

      {/* Organization indicator - only show in full mode */}
      {!compact && ims.org && (
        <div className={style({ display: 'flex', alignItems: 'center', gap: 8 })} style={{ marginTop: 'var(--spectrum-global-dimension-size-150)' }}>
          <Text UNSAFE_className="org-label">
            ORG:
          </Text>
          <Text UNSAFE_className="org-value">
            {ims.org}
          </Text>
        </div>
      )}
    </>
  )
}
